/**
 * MEMarketingModule — Marketing tab for MindEscapes (hospitality).
 *
 * Distinct from the TDB retail marketing module because ME's funnel is:
 *   Enquiry → Booking → Stay → Review
 *
 * Sections:
 *   1. Totals strip: Enquiries · Bookings Won · Conversion % · Direct vs OTA %
 *   2. Enquiry Sources table (with won/lost/pipeline/conversion)
 *   3. OTA vs Direct ratio + commission leakage estimate
 *   4. Review scores per outlet × platform
 *
 * Backend: GET /api/marketing/me-dashboard/{division_id}
 */
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Megaphone, Star, TrendingUp, AlertTriangle } from 'lucide-react';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from 'recharts';

const INR = (n) => `₹${Math.round(n).toLocaleString('en-IN')}`;
const LAKH = (n) => (n >= 1e5 ? `₹${(n / 1e5).toFixed(2)}L` : INR(n));

export default function MEMarketingModule({ divisionId, divisionName }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    setLoading(true);
    api.get(`/marketing/me-dashboard/${divisionId}`)
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.detail || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [divisionId]);

  if (loading) return <div className="p-8 text-center text-sm text-[#6B7280]">Loading…</div>;
  if (err) return <div className="p-8 text-center text-sm text-[#D4726A]">{err}</div>;
  if (!data) return null;

  const { totals, enquiries_by_source, ota_vs_direct, review_scores, review_summary } = data;

  const pieData = [
    { name: 'Direct', value: ota_vs_direct.direct.enquiries, color: '#2D8B5E' },
    { name: 'OTA', value: ota_vs_direct.ota.enquiries, color: '#D4726A' },
  ];

  return (
    <div className="space-y-4" data-testid="me-marketing-module">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Megaphone size={18} style={{ color: '#2A7B88' }} />
        <div>
          <h2 className="text-lg font-bold" style={{ color: '#1B2A4A' }}>Marketing · {divisionName}</h2>
          <p className="text-xs" style={{ color: '#6B7280' }}>Hospitality funnel — enquiries, sources, OTA vs Direct, reviews</p>
        </div>
      </div>

      {/* Totals strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Total Enquiries" value={totals.enquiries.toLocaleString('en-IN')} accent="#1B2A4A" testid="me-stat-enquiries" />
        <Stat label="Bookings Won" value={totals.bookings_won.toLocaleString('en-IN')} accent="#2D8B5E" testid="me-stat-won"
          sub={`${totals.conversion_pct}% conversion`} />
        <Stat label="Direct Revenue" value={LAKH(totals.revenue_direct)} accent="#2A7B88" testid="me-stat-direct"
          sub={`${totals.direct_pct}% of enquiries`} />
        <Stat label="OTA Revenue" value={LAKH(totals.revenue_ota)} accent="#BA7517" testid="me-stat-ota"
          sub={`${totals.ota_pct}% of enquiries`} />
      </div>

      {/* OTA vs Direct split */}
      <div className="grid md:grid-cols-2 gap-3">
        <Card title="OTA vs Direct Mix" subtitle="By enquiry volume">
          {totals.enquiries === 0 ? (
            <p className="text-sm text-center py-8" style={{ color: '#9CA3AF' }}>No enquiries recorded yet.</p>
          ) : (
            <div className="flex items-center gap-4">
              <div style={{ width: 160, height: 160 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={40} outerRadius={70} paddingAngle={2}>
                      {pieData.map((e, i) => <Cell key={i} fill={e.color} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-2">
                <LegendRow color="#2D8B5E" label="Direct" pct={totals.direct_pct} count={ota_vs_direct.direct.enquiries} revenue={ota_vs_direct.direct.revenue} />
                <LegendRow color="#D4726A" label="OTA" pct={totals.ota_pct} count={ota_vs_direct.ota.enquiries} revenue={ota_vs_direct.ota.revenue} />
              </div>
            </div>
          )}
        </Card>

        <Card title="Commission Leakage (est.)" subtitle="OTA revenue × 18% avg commission">
          <div className="flex items-start gap-2 p-3 rounded-md" style={{ backgroundColor: '#FEF3C7' }}>
            <AlertTriangle size={16} style={{ color: '#BA7517' }} className="shrink-0 mt-0.5" />
            <div>
              <p className="text-2xl font-bold" style={{ color: '#BA7517' }} data-testid="me-stat-leakage">
                {LAKH(totals.commission_leakage_est)}
              </p>
              <p className="text-[11px] mt-1" style={{ color: '#6B7280' }}>
                Est. commission paid to OTAs. Drive more direct bookings (WhatsApp, Website, Referral) to recapture this.
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Enquiries by Source */}
      <Card title="Enquiries by Source" subtitle="Funnel conversion per channel">
        {enquiries_by_source.length === 0 ? (
          <p className="text-sm text-center py-6" style={{ color: '#9CA3AF' }}>No enquiries yet. Log bookings via the Bookings tab.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="me-enquiry-source-table">
              <thead>
                <tr style={{ backgroundColor: '#F7F5F2' }} className="text-left">
                  <th className="px-3 py-2 font-semibold" style={{ color: '#6B7280' }}>Source</th>
                  <th className="px-3 py-2 font-semibold" style={{ color: '#6B7280' }}>Type</th>
                  <th className="px-3 py-2 font-semibold text-right" style={{ color: '#6B7280' }}>Total</th>
                  <th className="px-3 py-2 font-semibold text-right" style={{ color: '#6B7280' }}>Won</th>
                  <th className="px-3 py-2 font-semibold text-right" style={{ color: '#6B7280' }}>Lost</th>
                  <th className="px-3 py-2 font-semibold text-right" style={{ color: '#6B7280' }}>Pipeline</th>
                  <th className="px-3 py-2 font-semibold text-right" style={{ color: '#6B7280' }}>Conv %</th>
                  <th className="px-3 py-2 font-semibold text-right" style={{ color: '#6B7280' }}>Revenue</th>
                </tr>
              </thead>
              <tbody>
                {enquiries_by_source.map((r, i) => (
                  <tr key={i} className="border-t" style={{ borderColor: '#F3F4F6' }} data-testid={`me-enquiry-row-${i}`}>
                    <td className="px-3 py-2 font-medium" style={{ color: '#1B2A4A' }}>{r.source}</td>
                    <td className="px-3 py-2">
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: r.classification === 'ota' ? '#FEF3C7' : '#DCFCE7', color: r.classification === 'ota' ? '#92400E' : '#166534' }}>
                        {r.classification.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-bold" style={{ color: '#1B2A4A' }}>{r.count}</td>
                    <td className="px-3 py-2 text-right" style={{ color: '#2D8B5E' }}>{r.won}</td>
                    <td className="px-3 py-2 text-right" style={{ color: '#D4726A' }}>{r.lost}</td>
                    <td className="px-3 py-2 text-right" style={{ color: '#6B7280' }}>{r.pipeline}</td>
                    <td className="px-3 py-2 text-right font-bold" style={{ color: r.conversion_pct >= 30 ? '#2D8B5E' : r.conversion_pct >= 15 ? '#BA7517' : '#D4726A' }}>
                      {r.conversion_pct}%
                    </td>
                    <td className="px-3 py-2 text-right" style={{ color: '#1B2A4A' }}>{LAKH(r.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Reviews */}
      <Card title="Review Scores" subtitle={`${review_summary.total_reviews.toLocaleString('en-IN')} reviews across platforms — avg ${review_summary.avg_rating}★`}>
        {review_scores.length === 0 ? (
          <p className="text-sm text-center py-6" style={{ color: '#9CA3AF' }}>
            No reviews tracked yet. Add entries to the <code className="px-1 py-0.5 rounded bg-[#F7F5F2] text-[10px]">outlet_reviews</code> collection
            (platform: Google / Booking.com / MMT / Agoda; outlet; rating; review_count).
          </p>
        ) : (
          <div className="space-y-3">
            {/* Per-outlet summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {review_summary.by_outlet.map((o, i) => (
                <div key={i} className="p-3 rounded-md border" style={{ borderColor: '#E5E1DC', backgroundColor: '#FAFAF7' }} data-testid={`me-review-outlet-${i}`}>
                  <p className="text-[10px] uppercase tracking-wide" style={{ color: '#6B7280' }}>{o.outlet}</p>
                  <p className="text-lg font-bold flex items-center gap-1 mt-1" style={{ color: o.avg_rating >= 4.5 ? '#2D8B5E' : o.avg_rating >= 4 ? '#BA7517' : '#D4726A' }}>
                    <Star size={14} fill="currentColor" /> {o.avg_rating}
                  </p>
                  <p className="text-[10px]" style={{ color: '#9CA3AF' }}>
                    {o.total_reviews} reviews · {o.platforms} platforms
                  </p>
                </div>
              ))}
            </div>

            {/* Per-platform rows */}
            <table className="w-full text-xs" data-testid="me-review-table">
              <thead>
                <tr style={{ backgroundColor: '#F7F5F2' }} className="text-left">
                  <th className="px-3 py-2 font-semibold" style={{ color: '#6B7280' }}>Outlet</th>
                  <th className="px-3 py-2 font-semibold" style={{ color: '#6B7280' }}>Platform</th>
                  <th className="px-3 py-2 font-semibold text-right" style={{ color: '#6B7280' }}>Rating</th>
                  <th className="px-3 py-2 font-semibold text-right" style={{ color: '#6B7280' }}>Reviews</th>
                </tr>
              </thead>
              <tbody>
                {review_scores.map((r, i) => (
                  <tr key={i} className="border-t" style={{ borderColor: '#F3F4F6' }}>
                    <td className="px-3 py-2" style={{ color: '#1B2A4A' }}>{r.outlet}</td>
                    <td className="px-3 py-2" style={{ color: '#6B7280' }}>{r.platform}</td>
                    <td className="px-3 py-2 text-right font-bold" style={{ color: r.rating >= 4.5 ? '#2D8B5E' : r.rating >= 4 ? '#BA7517' : '#D4726A' }}>
                      {r.rating}★
                    </td>
                    <td className="px-3 py-2 text-right" style={{ color: '#6B7280' }}>{r.review_count.toLocaleString('en-IN')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function Stat({ label, value, sub, accent, testid }) {
  return (
    <div className="rounded-lg border p-3" style={{ borderColor: '#E5E1DC', backgroundColor: '#FFFFFF' }} data-testid={testid}>
      <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: '#6B7280' }}>{label}</p>
      <p className="text-2xl font-bold mt-1" style={{ color: accent }}>{value}</p>
      {sub && <p className="text-[10px] mt-1" style={{ color: '#9CA3AF' }}>{sub}</p>}
    </div>
  );
}

function Card({ title, subtitle, children }) {
  return (
    <div className="rounded-lg border p-4" style={{ borderColor: '#E5E1DC', backgroundColor: '#FFFFFF' }}>
      <div className="mb-3">
        <h3 className="text-sm font-bold" style={{ color: '#1B2A4A' }}>{title}</h3>
        {subtitle && <p className="text-[11px]" style={{ color: '#9CA3AF' }}>{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function LegendRow({ color, label, pct, count, revenue }) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
      <div>
        <p className="text-xs font-semibold" style={{ color: '#1B2A4A' }}>{label} — {pct}%</p>
        <p className="text-[10px]" style={{ color: '#9CA3AF' }}>{count} enq · {LAKH(revenue)} revenue</p>
      </div>
    </div>
  );
}
